<script setup lang="ts">
  // Read-only preview of a table's pending orders with pill tabs when the
  // table holds more than one (plan §5.4). Opened via showDynamicDialog from
  // the floor-plan tile long-press / right-click. Data is live from the
  // socket-fed ordersStore — no fetch needed (unlike the -pos original).
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { OrderType } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { useModal } from '@/composition/use-modal';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosV2ViewWorkspaceBase from '@/components/pos-v2/view/PosV2ViewWorkspaceBase.vue';

  const props = defineProps<{
    tableId: number;
  }>();

  const MODAL_NAME = 'pos-v2-table-orders-preview';

  const modals = useModal();
  const ordersStore = useOrdersStore();
  const restaurantStore = useRestaurantStore();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const tableName = computed(
    () =>
      restaurantStore.tables?.find((t) => t.id === props.tableId)?.name ?? ''
  );

  const title = computed(() =>
    tableName.value
      ? i18nT('posV2.tableOrdersPreviewTitle', { name: tableName.value })
      : i18nT('posV2.orderPreviewTitle')
  );

  const isLoading = computed(() => !ordersStore.pendingOrdersByType);

  const tableOrders = computed<IOrder[]>(() => {
    const seatOrders = ordersStore.pendingOrdersByType?.[OrderType.Seat] ?? [];
    return seatOrders.filter((o) => o.tableId === props.tableId);
  });

  const selectedIndex = ref(0);

  // Orders can leave the table live (paid, archived, moved): keep the
  // selection inside bounds.
  watch(tableOrders, (orders) => {
    if (selectedIndex.value >= orders.length) {
      selectedIndex.value = Math.max(0, orders.length - 1);
    }
  });

  const selectedOrder = computed<IOrder | null>(
    () => tableOrders.value[selectedIndex.value] ?? null
  );

  const orderTabs = computed(() =>
    tableOrders.value.map((order, index) => ({
      index,
      label:
        String(order.userName ?? '').trim() ||
        i18nT('posV2.orderIndexTab', { n: index + 1 }),
    }))
  );

  function handleClose() {
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="pos-v2-table-orders-preview-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ title }}</h2>
      <PosUiButton
        icon="close"
        color="neutral"
        variant="ghost"
        size="md"
        @click="handleClose"
      />
    </div>

    <div class="modal-body">
      <PosUiEmptyState
        v-if="!isLoading && tableOrders.length === 0"
        icon="inbox-outline"
        :title="$t('posV2.noOrdersOnTable')"
        class="empty-state"
      />

      <template v-else>
        <div v-if="orderTabs.length > 1" class="order-tabs">
          <PosUiButton
            v-for="tab in orderTabs"
            :key="tab.index"
            :color="tab.index === selectedIndex ? 'primary' : 'neutral'"
            :variant="tab.index === selectedIndex ? 'solid' : 'outline'"
            size="md"
            class="order-tab"
            @click="selectedIndex = tab.index"
          >
            {{ tab.label }}
          </PosUiButton>
        </div>

        <PosV2ViewWorkspaceBase
          :order="selectedOrder"
          :loading="isLoading"
          mode="preview"
        />
      </template>
    </div>

    <div class="modal-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="lg"
        class="btn-close"
        @click="handleClose"
      >
        {{ $t('posV2.close') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Root lives inside .vm--modal.dialog--height-limit (a max-height flex
  // parent): the body asks for 76vh and shrinks on short screens.
  .pos-v2-table-orders-preview-modal {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col;

    .modal-header {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }
    }

    .modal-body {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden;
      height: 76vh;

      .empty-state {
        @apply tw-flex-1;
      }

      .order-tabs {
        @apply tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-neutral-200 tw-bg-neutral-50 tw-px-4 tw-py-2.5;

        :deep(.order-tab) {
          @apply tw-min-w-[110px] tw-justify-center;
        }
      }
    }

    .modal-footer {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-end tw-border-t tw-border-neutral-200 tw-px-4 tw-py-3;

      :deep(.btn-close) {
        @apply tw-min-w-[150px];
      }
    }
  }
</style>
